<template>
    <div class="praise-modal__delivery">
        <PraiseModalDeliveryMethod
            v-for="method in sendMethods"
            :key="method.key"
            v-model:selected-send-method="selectedSendMethod"
            :method="method"
            @change="onSendMethodChanged"
        >
            <div
                v-if="showExtraMethodField(method, 'text')"
                class="praise-modal__delivery__content"
            >
                <PraiseModalDeliveryEmail
                    v-model:emails="emails"
                    :praisee-email="praiseeEmail"
                    :is-invalid="hasInvalidEmail"
                    :error-msg="errorMsg"
                />
            </div>
            <Select
                v-if="
                    showExtraMethodField(method, 'channel-dropdown') &&
                    method.data.channelList.length
                "
                v-model="selectedSendMethodData"
                :options="method.data.channelList"
                option-label="name"
                placeholder="Select Channel"
                class="praise-modal__select-input"
            />
        </PraiseModalDeliveryMethod>

        <Transition name="fade">
            <div
                v-if="showTagsToSendMethodMismatchMessage"
                class="tags-send-method-mismatch-message"
            >
                Tags and hashtags will not be applied in
                {{ getSendMethodLabel(selectedSendMethod) }}
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef } from 'vue'
import Select from 'primevue/select'
import PraiseModalDeliveryEmail from '@/components/FrontlineManagerDash/PraiseModal/PraiseModalDeliveryEmail.vue'
import PraiseModalDeliveryMethod from '@/components/FrontlineManagerDash/PraiseModal/PraiseModalDeliveryMethod.vue'
import {
    SEND_METHOD_EMAIL,
    SEND_METHOD_INTERNAL_NOTE,
    SendMethod,
} from '@/entities/frontlineManagerDash'
import { faEnvelope, faMessageLines } from 'fontawesome/pro-regular-svg-icons'
import { getSendMethodLabel } from '@/utils/frontlineManagerDash'

interface Props {
    installedApps: SendMethod[]
    praiseeEmail: string | undefined
    hasInvalidEmail?: boolean
    errorMsg?: string
    hasMentionsOrTags: boolean
}
const props = defineProps<Props>()

const emails = defineModel('emails', {
    type: String,
    default: '',
})
const selectedSendMethod = defineModel('selectedSendMethod', {
    type: String,
    default: SEND_METHOD_INTERNAL_NOTE,
})
const selectedSendMethodData = defineModel('selectedSendMethodData', {
    type: {},
    default: {},
})

const emit = defineEmits(['sendMethodChanged'])

const sendMethods: ComputedRef<SendMethod[]> = computed(() => {
    let availableMethods: SendMethod[] = [
        {
            key: SEND_METHOD_INTERNAL_NOTE,
            label: 'Internal Note',
            faIcon: faMessageLines,
        },
        ...props.installedApps,
        {
            key: SEND_METHOD_EMAIL,
            label: 'Email',
            faIcon: faEnvelope,
            extraFieldType: 'text',
            toolTipMsg:
                "Additional recipients will be CC'd. Emails must be separated with ',' or ';'",
        },
    ]
    return availableMethods
})

const onSendMethodChanged = (value, label: string) => {
    emit('sendMethodChanged', value, label)
}

const showExtraMethodField = (method: SendMethod, fieldType: string) => {
    return (
        method.key === selectedSendMethod.value &&
        method.extraFieldType === fieldType
    )
}

const showTagsToSendMethodMismatchMessage = computed(() => {
    return (
        props.hasMentionsOrTags &&
        selectedSendMethod.value !== SEND_METHOD_INTERNAL_NOTE
    )
})
</script>

<style lang="less">
// Override UiKit ul styles
ul.p-select-list {
    margin: unset !important;
    padding: unset !important;
}
</style>

<style lang="less" scoped>
@import '@/styles/prime-vue/variables.less';
@import '@/styles/prime-vue/colours.less';
@import '@/styles/rain/variables.less';

.praise-modal__delivery {
    display: flex;
    flex-direction: column;
    gap: @spacing-lg;
    margin-bottom: @spacing-3xl;

    &__content {
        flex: 1;
        display: flex;
    }
}

.praise-modal__select-input {
    font-size: @fontSize-md;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    max-height: 300px;

    :deep(.p-select-label) {
        // Workaround to avoid this label from exceeding the container width
        width: 1px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
.tags-send-method-mismatch-message {
    font-size: @fontSize-base;
    line-height: @lineHeight-base;
    color: @text-tertiary;
    margin-top: 8px;
    text-align: center;

    overflow: hidden;
    interpolate-size: allow-keywords;
}

.fade-enter-active,
.fade-leave-active {
    transition:
        opacity,
        height 0.2s linear;
    height: fit-content;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0;
}
</style>
